import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import ClinqLogo from "../components/ClinqLogo";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function AdvisorConfirm() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/advisor-confirm/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setInfo({ status: "invalid", detail: e?.response?.data?.detail }));
  }, [token]);

  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/public/advisor-confirm/${token}`);
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't confirm — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#04302E] text-white" style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}>
      <nav className="border-b border-white/5 px-6 py-4">
        <Link to="/" className="block w-fit"><ClinqLogo /></Link>
      </nav>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center" data-testid="advisor-confirm-card">
          {!info ? (
            <p className="flex items-center justify-center gap-2 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Checking your link…</p>
          ) : done || info.status === "already_confirmed" ? (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
              <h1 className="mt-4 text-xl font-extrabold" data-testid="advisor-confirm-success">Advisor access confirmed</h1>
              <p className="mt-2 text-sm text-white/70">
                {info.advisor_name} is now authorised on your account. Their access begins once the Clinq team has also approved them.
              </p>
            </>
          ) : info.status === "expired" ? (
            <>
              <AlertTriangle className="mx-auto h-12 w-12 text-amber-400" />
              <h1 className="mt-4 text-xl font-extrabold">This link has expired</h1>
              <p className="mt-2 text-sm text-white/70">Confirmation links are valid for 7 days. Please ask your advisor to re-send the request.</p>
            </>
          ) : info.status === "revoked" || info.status === "invalid" ? (
            <>
              <AlertTriangle className="mx-auto h-12 w-12 text-rose-400" />
              <h1 className="mt-4 text-xl font-extrabold">Link not valid</h1>
              <p className="mt-2 text-sm text-white/70">This confirmation link is no longer valid. If you weren't expecting this, you can safely ignore it — or contact support@clinq.africa.</p>
            </>
          ) : (
            <>
              <h1 className="text-xl font-extrabold" data-testid="advisor-confirm-title">Authorise your advisor</h1>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                <b className="text-white">{info.advisor_name}</b> has requested access to your Clinq account
                {info.client_name ? <> for <b className="text-white">{info.client_name}</b></> : null}.
                If you authorise this, click confirm below. If you did not expect this request,
                simply ignore it or contact <a className="text-emerald-300 underline" href="mailto:support@clinq.africa">support@clinq.africa</a>.
              </p>
              {err && <p className="mt-3 text-sm text-rose-300">{err}</p>}
              <button type="button" data-testid="advisor-confirm-btn" disabled={busy} onClick={confirm}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-8 py-3 text-sm font-bold text-[#04302E] hover:bg-emerald-400 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm advisor access"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
